// EquipmentManager.js
import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTheme } from "@mui/material/styles";
import { getEquipments, addEquipment, removeEquipment } from "../api/equipment";

// Função utilitária para formatar o MAC
function formatMac(mac) {
  return (mac.replace(/[^A-Fa-f0-9]/g, "")
    .toUpperCase()
    .match(/.{1,2}/g) || []).join(":");
}

export default function EquipmentManager({
  selectedMac,
  setSelectedMac,
  setAmbienteNome,
  setTab,

  // ✅ novos flags (com defaults)
  autoSelectFirst = true,
  autoGoToDashboard = true,
}) {
  const [equipments, setEquipments] = useState([]);
  const [mac, setMac] = useState("");
  const [location, setLocation] = useState("");
  const [msg, setMsg] = useState({ open: false, text: "", severity: "success" });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const goToDashboard = () => {
    if (typeof setTab === "function") setTab(0);
  };

  // Busca equipamentos e normaliza MACs
  const loadEquipments = async () => {
    try {
      let list = await getEquipments();
      list = list.map((e) => ({ ...e, mac: formatMac(e.mac) }));
      setEquipments(list);

      // ✅ auto select opcional
      if (autoSelectFirst && list.length && !selectedMac) {
        setSelectedMac(list[0].mac);
        if (setAmbienteNome) setAmbienteNome(list[0].location);

        // se auto-select, NÃO força trocar de aba (pra não “puxar” externo)
        // quem controla aba é o pai.
      }
    } catch (e) {
      setMsg({ open: true, text: "Erro ao buscar equipamentos", severity: "error" });
    }
  };

  useEffect(() => {
    loadEquipments();
    // eslint-disable-next-line
  }, []);

  // Atualiza nome do ambiente ao selecionar um novo equipamento
  useEffect(() => {
    if (!equipments.length) return;
    const current = equipments.find((e) => e.mac === selectedMac);
    if (current && setAmbienteNome) setAmbienteNome(current.location);
  }, [selectedMac, equipments, setAmbienteNome]);

  const handleAdd = async () => {
    const macFormatted = formatMac(mac);

    if (!macFormatted || macFormatted.length !== 17) {
      return setMsg({
        open: true,
        text: "MAC inválido! Use o formato AA:BB:CC:DD:EE:FF.",
        severity: "warning",
      });
    }
    if (!location) {
      return setMsg({ open: true, text: "Preencha o local", severity: "warning" });
    }

    try {
      const data = await addEquipment(macFormatted, location);

      setMsg({
        open: true,
        text: data.message || "Adicionado!",
        severity: "success",
      });

      setMac("");
      setLocation("");

      // ✅ Seleciona o que acabou de cadastrar
      setSelectedMac(macFormatted);
      if (setAmbienteNome) setAmbienteNome(location);

      // ✅ só vai pro painel se flag permitir
      if (autoGoToDashboard) goToDashboard();

      loadEquipments();
    } catch (e) {
      setMsg({ open: true, text: "Erro ao adicionar (já cadastrado?)", severity: "error" });
    }
  };

  const handleRemove = async (macRemove) => {
    try {
      const data = await removeEquipment(macRemove);

      setMsg({
        open: true,
        text: data.message || "Removido!",
        severity: "success",
      });

      if (selectedMac === macRemove) {
        setSelectedMac("");
        if (setAmbienteNome) setAmbienteNome("");
      }

      loadEquipments();
    } catch (e) {
      setMsg({ open: true, text: "Erro ao remover", severity: "error" });
    }
  };

  const handleSelectChange = (newMac) => {
    setSelectedMac(newMac);

    const current = equipments.find((e) => e.mac === newMac);
    if (current && setAmbienteNome) setAmbienteNome(current.location);

    // ✅ Selecionou → vai pro Painel somente se flag permitir
    if (autoGoToDashboard) goToDashboard();
  };

  return (
    <Box
      sx={{
        bgcolor: theme.palette.background.paper,
        borderRadius: 2,
        boxShadow: 2,
        p: { xs: 1, sm: 3 },
        mb: 3,
        width: "100%",
        maxWidth: 650,
        mx: "auto",
      }}
    >
      <Typography variant={isMobile ? "subtitle1" : "h6"} mb={2}>
        Gerenciar Equipamentos/Ambientes
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 2 },
          mb: 2,
        }}
      >
        <TextField
          label="MAC do Equipamento"
          value={mac}
          onChange={(e) => setMac(formatMac(e.target.value))}
          size={isMobile ? "small" : "medium"}
          sx={{ flex: 1 }}
          inputProps={{ maxLength: 17 }}
          placeholder="AA:BB:CC:DD:EE:FF"
        />

        <TextField
          label="Nome do Ambiente"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          size={isMobile ? "small" : "medium"}
          sx={{ flex: 1, mt: { xs: 1, sm: 0 } }}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={handleAdd}
          sx={{ minWidth: isMobile ? "100%" : 120, mt: { xs: 1, sm: 0 } }}
        >
          Adicionar
        </Button>
      </Box>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Selecione o Ambiente</InputLabel>
        <Select
          value={selectedMac}
          label="Selecione o Ambiente"
          onChange={(e) => handleSelectChange(e.target.value)}
          size={isMobile ? "small" : "medium"}
        >
          {equipments.map((equip) => (
            <MenuItem key={equip.mac} value={equip.mac}>
              {equip.location} ({equip.mac})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <List
        sx={{
          width: "100%",
          maxHeight: isMobile ? 240 : 320,
          overflowY: "auto",
          bgcolor: "background.paper",
          borderRadius: 2,
          p: 0,
          boxShadow: 1,
        }}
      >
        {equipments.map((equip) => (
          <ListItem
            key={equip.mac}
            secondaryAction={
              <IconButton edge="end" aria-label="delete" color="error" onClick={() => handleRemove(equip.mac)}>
                <DeleteIcon />
              </IconButton>
            }
            selected={selectedMac === equip.mac}
            sx={{
              px: 2,
              py: 1,
              "&.Mui-selected": { bgcolor: theme.palette.action.selected },
              cursor: "pointer",
            }}
            onClick={() => handleSelectChange(equip.mac)}
          >
            <ListItemText
              primary={equip.location}
              secondary={equip.mac}
              primaryTypographyProps={{ sx: { fontSize: isMobile ? 15 : 17 } }}
              secondaryTypographyProps={{ sx: { fontSize: isMobile ? 12 : 13 } }}
            />
          </ListItem>
        ))}
      </List>

      <Snackbar open={msg.open} autoHideDuration={2000} onClose={() => setMsg({ ...msg, open: false })}>
        <Alert severity={msg.severity}>{msg.text}</Alert>
      </Snackbar>
    </Box>
  );
}
